import React from 'react';
import { Row, Col, Card, Statistic, Typography, Space, Button } from 'antd';
import { 
  ProjectOutlined, 
  CheckSquareOutlined, 
  ClockCircleOutlined,
  TeamOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import RecentProjects from './components/RecentProjects';
import MyTasksSummary from './components/MyTasksSummary';
import WorkloadChart from './components/WorkloadChart';

const { Title } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // 获取统计数据
  const { data: projectStats } = useQuery(
    'projectStats',
    () => axios.get('/projects/stats').then(res => res.data.data),
    { refetchInterval: 30000 } // 30秒刷新一次
  );

  const { data: taskStats } = useQuery(
    'taskStats',
    () => axios.get('/tasks/stats').then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  const { data: workStats } = useQuery(
    'workStats',
    () => axios.get('/daily-logs/stats', {
      params: {
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
      }
    }).then(res => res.data.data),
    { refetchInterval: 60000 } // 1分钟刷新一次
  );

  const getProjectStatusCount = (status) => {
    return projectStats?.statusStats?.find(s => s.status === status)?.count || 0;
  };

  const getTaskStatusCount = (status) => {
    return taskStats?.statusStats?.find(s => s.status === status)?.count || 0;
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 欢迎信息 */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          欢迎回来，{user?.realName || user?.username}！
        </Title>
        <Typography.Text type="secondary">
          今天是 {new Date().toLocaleDateString('zh-CN', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            weekday: 'long'
          })}
        </Typography.Text>
      </div>

      {/* 核心指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="status-card">
            <Statistic
              title="进行中项目"
              value={getProjectStatusCount('designing') + getProjectStatusCount('planning')}
              prefix={<ProjectOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Button 
              type="link" 
              size="small"
              onClick={() => navigate('/projects')}
            >
              查看详情
            </Button>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="status-card">
            <Statistic
              title="我的待办任务"
              value={getTaskStatusCount('pending') + getTaskStatusCount('in_progress')}
              prefix={<CheckSquareOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Button 
              type="link" 
              size="small"
              onClick={() => navigate('/my-tasks')}
            >
              立即处理
            </Button>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="status-card">
            <Statistic
              title="本周工作时长"
              value={workStats?.workStats?.[0]?.totalHours || 0}
              precision={1}
              suffix="小时"
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
            <Button 
              type="link" 
              size="small"
              onClick={() => navigate('/daily-log')}
            >
              填写日志
            </Button>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="status-card">
            <Statistic
              title="项目总预算"
              value={projectStats?.totalBudget || 0}
              precision={0}
              suffix="元"
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 主要内容区域 */}
      <Row gutter={[16, 16]}>
        {/* 左侧：我的任务和最近项目 */}
        <Col xs={24} lg={16}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <MyTasksSummary />
            <RecentProjects />
          </Space>
        </Col>

        {/* 右侧：工作负荷图表 */}
        <Col xs={24} lg={8}>
          <WorkloadChart />
        </Col>
      </Row>

      {/* 快捷操作 */}
      {['admin', 'manager', 'pm'].includes(user?.role) && (
        <Card 
          title="快捷操作" 
          style={{ marginTop: '24px' }}
          className="status-card"
        >
          <Space wrap>
            <Button 
              type="primary" 
              icon={<ProjectOutlined />}
              onClick={() => navigate('/projects')}
            >
              创建新项目
            </Button>
            <Button 
              icon={<CheckSquareOutlined />}
              onClick={() => navigate('/tasks')}
            >
              分配任务
            </Button>
            <Button 
              icon={<BarChartOutlined />}
              onClick={() => navigate('/analytics/team-workload')}
            >
              查看团队负荷
            </Button>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
